import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../components/Icon';
import WarehousePicker from '../components/WarehousePicker';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { fmtShortTime, fmtShort as fmtShort2 } from '../utils/date';
import { useLanguage } from '../context/LanguageContext';
import './Procurement.css';

const STATUS_COLORS = {
  pending_head:     { color: '#d97706', bg: '#fef3c7' },
  pending_finance:  { color: '#7c3aed', bg: '#ede9fe' },
  pending_director: { color: '#0369a1', bg: '#e0f2fe' },
  approved:         { color: '#059669', bg: '#d1fae5' },
  in_process:       { color: '#0369a1', bg: '#e0f2fe' },
  delivered:        { color: '#059669', bg: '#d1fae5' },
  completed:        { color: '#374151', bg: '#e5e7eb' },
  rejected:         { color: '#dc2626', bg: '#fee2e2' },
};

const UOM_VALUES = ['ədəd', 'bağlama', 'rulon', 'dəst', 'metr', 'kq', 'litr', 'paket', 'qutu', 'digər'];

/* Taymer icra başlayandan sayır. Başlamayıbsa sayğac göstərilmir —
   əvvəl direktorun təsdiqindən saymağa başlayırdı və təchizatçı sorğunu
   hələ açmamış "gecikmiş" görünürdü. */
function DeliveryTimer({ startedAt, deliveredAt, neededBy, dayLabel, notStartedLabel }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt) return;
    const end = deliveredAt ? new Date(deliveredAt) : null;
    const tick = () => {
      const diff = ((end || new Date()) - new Date(startedAt)) / 1000;
      setElapsed(Math.max(0, Math.round(diff)));
    };
    tick();
    if (!end) { const iv = setInterval(tick, 1000); return () => clearInterval(iv); }
  }, [startedAt, deliveredAt]);
  const d = Math.floor(elapsed / 86400);
  const h = Math.floor((elapsed % 86400) / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  const pad = n => String(n).padStart(2, '0');
  const timeStr = (d > 0 ? d + ' ' + (dayLabel || 'gün') + ' ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);

  // Deadline check
  const isOverdue = neededBy && !deliveredAt && new Date() > new Date(neededBy);
  const isDeliveredLate = neededBy && deliveredAt && new Date(deliveredAt) > new Date(neededBy);

  return (
    <div className="timer-wrap">
      {!startedAt ? (
        <span className="delivery-timer idle">
          <Icon name="clock" size={13} />{' '}{notStartedLabel || 'Başlamayıb'}
        </span>
      ) : (
        <span className={'delivery-timer' + (deliveredAt ? ' done' : '') + (isOverdue ? ' overdue' : '')}>
          {deliveredAt ? <Icon name="check" size={13} /> : <Icon name="clock" size={13} />}{' '}{timeStr}
        </span>
      )}
      {neededBy && (
        <span className={'deadline-badge' + (isOverdue || isDeliveredLate ? ' late' : deliveredAt ? ' ok' : '')}>
          {isOverdue ? 'Gecikir!' : isDeliveredLate ? 'Gecikmə ilə' : deliveredAt ? <><Icon name="check" size={13} /> Vaxtında</> : 'Son: ' + new Date(neededBy).toLocaleDateString('az-AZ')}
        </span>
      )}
    </div>
  );
}

function ApprovalSteps({ req, t, lang }) {
  const fmtD = d => fmtShortTime(d, lang);
  const tApprovedLabel = t('procurement.timelineApproved');
  const tCreated = t('procurement.timelineCreated');
  const steps = [
    { key:'created', label:t('procurement.stageOrderer'),  desc:tCreated, doneDesc:tCreated, icon:'1', approved:true, date:req.created_at },
    { key:'head',    label:t('procurement.stageHead'),     desc:t('procurement.statusPendingHead'),     doneDesc:tApprovedLabel, icon:'2', approved:!!req.head_approved_at,    rejected:!!req.head_rejected,    date:req.head_approved_at, note:req.head_note },
    { key:'finance', label:t('procurement.stageFinance'),  desc:t('procurement.timelineFinanceApproved'), doneDesc:t('procurement.timelineFinanceApproved'), icon:'3', approved:!!req.finance_approved_at, rejected:!!req.finance_rejected, date:req.finance_approved_at, note:req.finance_note },
    { key:'director',label:t('procurement.stageDirector'), desc:t('procurement.timelineFinalApproval'), doneDesc:t('procurement.timelineFinalApproval'), icon:'4', approved:!!req.director_approved_at, rejected:!!req.director_rejected, date:req.director_approved_at, note:req.director_note },
    { key:'process', label:t('procurement.stagePurchase'),  desc:t('procurement.timelinePurchaseStarted'), doneDesc:t('procurement.timelinePurchaseStarted'), icon:'5', approved:['approved','in_process','delivered','completed'].includes(req.status), date:req.process_started_at },
    { key:'deliver', label:t('procurement.stageDelivered'),  desc:t('procurement.timelineDelivered'), doneDesc:t('procurement.timelineDelivered'), icon:'6', approved:['delivered','completed'].includes(req.status), date:req.delivered_at, note:req.delay_reason },
    { key:'complete',label:t('procurement.stageCompleted'),  desc:t('procurement.timelineCompleted'), doneDesc:t('procurement.timelineCompleted'), icon:'7', approved:req.status==='completed', date:req.status==='completed'?req.updated_at:null },
  ];
  const currentIdx = steps.findIndex(s => !s.approved && !s.rejected);
  const isRejected = steps.some(s => s.rejected);
  const tRejected = t('procurement.statusRejected');

  return (
    <div className="roadmap">
      {steps.map((s, i) => {
        const isDone = s.approved;
        const isActive = i === currentIdx && !isRejected;
        const isReject = s.rejected;
        return (
          <div key={s.key} className={'roadmap-step' + (isDone?' done':'') + (isActive?' active':'') + (isReject?' rejected':'')}>
            <div className="roadmap-line-wrap">
              <div className={'roadmap-dot' + (isDone?' done':'') + (isActive?' active':'') + (isReject?' rejected':'')}>
                {isReject ? <Icon name="close" size={14} /> : isDone ? <Icon name="check" size={14} /> : <span className="roadmap-icon">{s.icon}</span>}
              </div>
              {i < steps.length - 1 && <div className={'roadmap-line' + (isDone?' done':'')} />}
            </div>
            <div className="roadmap-content">
              <div className="roadmap-label">{s.label}</div>
              <div className="roadmap-desc">{isReject ? tRejected : isDone ? s.doneDesc : s.desc}</div>
              {s.date && <div className="roadmap-date">{fmtD(s.date)}</div>}
              {s.note && <div className="roadmap-note"><Icon name="message" size={13} /> {s.note}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const emptyItem = { item_description: '', item_code: '', uom: 'ədəd', quantity_requested: 1, unit_price: '', used_by: '', note: '' };

export default function Procurement() {
  const { t, lang } = useLanguage();
  const UOM_LABELS = {
    'ədəd': t('procurement.uomPiece'),
    'bağlama': t('procurement.uomBundle'),
    'rulon': t('procurement.uomRoll'),
    'dəst': t('procurement.uomSet'),
    'metr': t('procurement.uomMeter'),
    'kq':   t('procurement.uomKg'),
    'litr': t('procurement.uomLiter'),
    'paket':t('procurement.uomPack'),
    'qutu': t('procurement.uomBox'),
    'digər':t('procurement.uomOther'),
  };
  const STATUS_MAP = {
    pending_head:     { label: t('procurement.statusPendingHead'),     ...STATUS_COLORS.pending_head },
    pending_finance:  { label: t('procurement.statusPendingFinance'),  ...STATUS_COLORS.pending_finance },
    pending_director: { label: t('procurement.statusPendingDirector'), ...STATUS_COLORS.pending_director },
    approved:         { label: t('status.approved'),                   ...STATUS_COLORS.approved },
    in_process:       { label: t('status.in_progress'),                ...STATUS_COLORS.in_process },
    delivered:        { label: t('status.delivered'),                  ...STATUS_COLORS.delivered },
    completed:        { label: t('status.completed'),                  ...STATUS_COLORS.completed },
    rejected:         { label: t('procurement.statusRejected'),        ...STATUS_COLORS.rejected },
  };
  const TYPE_MAP = {
    purchase:  { label: t('procurement.purchaseOrder'),  short: t('procurement.purchaseOrder') },
    warehouse: { label: t('procurement.warehouseReq'),   short: t('procurement.warehouse') },
  };
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isDeptHead = user.role === 'department_head';

  const [departments, setDepartments] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fType, setFType] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fDept, setFDept] = useState('');
  const [fPeriod, setFPeriod] = useState('');
  const [fActive, setFActive] = useState(false);

  // Sifarişin vəziyyətini e-poçtla paylaşmaq
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTo, setShareTo] = useState('');
  const [shareNote, setShareNote] = useState('');
  const [shareBusy, setShareBusy] = useState(false);

  const doShare = async (e) => {
    e.preventDefault();
    if (!detail || !shareTo.trim()) return;
    setShareBusy(true);
    try {
      const r = await API.post('/procurement/' + detail.id + '/share', {
        to: shareTo.trim(), note: shareNote.trim() || null,
      });
      setShareOpen(false);
      alert(t('procurement.shareSent') + ' (' + r.data.sent + ')');
    } catch (err) {
      alert(err.response?.data?.message || t('procurement.shareFailed'));
    } finally { setShareBusy(false); }
  };
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);

  const [showNew, setShowNew] = useState(false);
  const [newType, setNewType] = useState('warehouse');
  const [newDept, setNewDept] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newNeededBy, setNewNeededBy] = useState('');
  const [newItems, setNewItems] = useState([{ ...emptyItem }]);
  const [submitting, setSubmitting] = useState(false);

  const [approveNote, setApproveNote] = useState('');
  const [showRate, setShowRate] = useState(false);
  const [rateVal, setRateVal] = useState(0);
  const [rateComment, setRateComment] = useState('');
  const [receiveQtys, setReceiveQtys] = useState({});

  // Anbar kataloqu — tələbnamədə məhsul seçimi üçün
  const [whItems, setWhItems] = useState([]);
  useEffect(() => {
    API.get('/procurement/warehouse-items')
      .then(r => setWhItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setWhItems([]));
  }, []);

  // Kataloqdan məhsul seçiləndə ad, kod və ölçü vahidi birlikdə dolur
  const pickWarehouseItem = (i, it) => setNewItems(p => p.map((row, idx) => idx === i ? {
    ...row,
    item_description: it.name,
    item_code: it.item_code || '',
    uom: UOM_VALUES.includes(it.uom) ? it.uom : row.uom,
    wh_stock: it.closing_qty,
  } : row));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (fType) p.type = fType;
      if (fStatus) p.status = fStatus;
      if (fDept) p.department = fDept;
      if (fPeriod) p.period = fPeriod;
      if (fActive) p.active = '1';
      const r = await API.get('/procurement', { params: p });
      /* Sorğular səhifəsindən kampus seçilib gəlinibsə (?branch=2),
         yalnız həmin kampusun tələbnamələri qalır */
      const br = new URLSearchParams(window.location.search).get('branch');
      setList(br ? (r.data || []).filter(x => String(x.branch_id) === String(br)) : r.data);
    } catch (e) {} finally { setLoading(false); }
  }, [fType, fStatus, fDept, fPeriod, fActive]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { API.get('/departments').then(r => setDepartments(r.data)).catch(() => {}); }, []);

  /* E-poçtdakı "Sənədə bax" linki /procurement?id=NN şəklindədir —
     səhifə açılan kimi həmin sifarişi açırıq ki, istifadəçi siyahıda axtarmasın. */
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('id');
    if (id) openDetail({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  // Don't auto-open form - always show list first

  const openDetail = async (pr) => {
    try {
      const r = await API.get('/procurement/' + pr.id);
      setDetail(r.data);
      setItems(r.data.items || []);
      setApproveNote('');
      setShowRate(false);
      const qtys = {};
      (r.data.items || []).forEach(it => { qtys[it.id] = it.quantity_released || it.quantity_requested; });
      setReceiveQtys(qtys);
    } catch (e) {}
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (!newDept.trim() || newItems.every(it => !it.item_description.trim())) return;
    setSubmitting(true);
    try {
      const validItems = newItems.filter(it => it.item_description.trim());
      await API.post('/procurement', {
        type: newType, department: newDept, notes: newNotes,
        subject: newSubject, needed_by: newNeededBy || null,
        items: validItems
      });
      setShowNew(false);
      setNewType('warehouse'); setNewDept(''); setNewNotes('');
      setNewSubject(''); setNewNeededBy('');
      setNewItems([{ ...emptyItem }]);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Xəta baş verdi');
    } finally { setSubmitting(false); }
  };

  const doApprove = async (stage) => {
    try {
      await API.put('/procurement/' + detail.id + '/approve/' + stage, { note: approveNote });
      openDetail(detail); load();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const doReject = async (stage) => {
    if (!approveNote.trim()) return alert('Rədd səbəbini yazın');
    try {
      await API.put('/procurement/' + detail.id + '/reject', { stage, note: approveNote });
      openDetail(detail); load();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  /* İcraya başlamaq. Taymer bu andan sayır — direktor təsdiqindən deyil. */
  const doStart = async () => {
    try {
      await API.put('/procurement/' + detail.id + '/start');
      openDetail(detail); load();
    } catch (e) {
      alert(e.response?.data?.message || e.response?.data?.error || 'Xəta');
    }
  };

  const doDeliver = async () => {
    let delay_reason = null;

    // Lazım olan tarix keçibsə səbəb MƏCBURİDİR — boş buraxıb keçmək olmur
    const late = detail.needed_by &&
      new Date() > new Date(new Date(detail.needed_by).setHours(23, 59, 59, 999));
    if (late) {
      const due = new Date(detail.needed_by).toLocaleDateString('az-AZ');
      for (;;) {
        const r = prompt(`Mal gecikib (son tarix: ${due}).\nÇatdırılmanı qeyd etmək üçün gecikmə səbəbini yazın:`, delay_reason || '');
        if (r === null) return;                       // imtina → heç nə dəyişmir
        if (r.trim().length >= 3) { delay_reason = r.trim(); break; }
        alert('Səbəb ən azı 3 simvol olmalıdır.');
        delay_reason = r;
      }
    }

    try {
      await API.put('/procurement/' + detail.id + '/deliver', { delay_reason });
      openDetail(detail); load();
    } catch (e) {
      alert(e.response?.data?.message || e.response?.data?.error || 'Xəta');
    }
  };

  const doReceive = async () => {
    try {
      const quantities = Object.entries(receiveQtys).map(([id, qty]) => ({ item_id: parseInt(id), quantity_released: parseInt(qty) }));
      await API.put('/procurement/' + detail.id + '/receive', { quantities });
      openDetail(detail); load();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const doRate = async () => {
    if (rateVal < 1) return alert('Qiymət seçin');
    try {
      await API.put('/procurement/' + detail.id + '/rate', { rating: rateVal, rating_comment: rateComment });
      setShowRate(false); openDetail(detail); load();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const addItemRow = () => setNewItems(p => [...p, { ...emptyItem }]);
  const removeItemRow = (i) => setNewItems(p => p.length > 1 ? p.filter((_, idx) => idx !== i) : p);
  const updateItem = (i, k, v) => setNewItems(p => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const fmt = (d) => d ? new Date(d).toLocaleString('az-AZ') : '—';
  const fmtShort = (d) => fmtShort2(d, lang);

  // Position-based approval permissions
  // Serverdəki FINANCE_APPROVER_POSITIONS ilə eyni olmalıdır
  const FINANCE_POSITIONS = ['Baş maliyyə rəhbəri', 'Maliyyə nəzarətçisi'];
  const DIRECTOR_POSITIONS = ['Kompleksin İcraçı direktoru', 'Məktəb direktoru'];
  const isFinanceApprover = isAdmin || FINANCE_POSITIONS.includes(user.position);
  const isDirectorApprover = isAdmin || DIRECTOR_POSITIONS.includes(user.position);

  const canApprove = (stage) => {
    if (!detail) return false;
    /* Rəhbər Heyəti: icazəni server verir — təsdiqləyən sorğu sahibinin
       birbaşa rəhbəridir, o isə şöbə rəhbəri olmaya bilər. */
    if (stage === 'head') return detail.status === 'pending_head'
      && (detail.can_approve_head === true || ((detail.can_approve_head === undefined) && (isAdmin || isDeptHead)));
    /* Rəhbər təsdiqini verən şəxs Maliyyə mərhələsini bağlaya bilməz,
       heç kim öz sorğusunu təsdiq edə bilməz — serverin qərarı. */
    if (stage === 'finance') return detail.status === 'pending_finance' && isFinanceApprover
      && detail.can_approve_finance !== false;
    if (stage === 'director') return detail.status === 'pending_director' && isDirectorApprover;
    return false;
  };

  return (
    <div className="proc-page">
      {/* Başlıq, filtrlər və siyahı bir panelin içindədir — Qəbul və
          Siniflər səhifələri ilə eyni dizayn dili. */}
      <div className="proc-panel">
      <div className="proc-header">
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <button onClick={() => navigate('/requests')} style={{background:'#f3f4f6',border:'none',borderRadius:8,padding:'6px 8px',cursor:'pointer',color:'#374151',display:'flex',alignItems:'center'}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <div>
            <h2>{t('procurement.title')}</h2>
            <div className="proc-sub">{t('procurement.subtitle')}</div>
          </div>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ {t('procurement.btnNew')}</button>
      </div>

      <div className="proc-filters">
        <button
          type="button"
          className={'proc-chip' + (fActive ? ' on' : '')}
          onClick={() => setFActive(v => !v)}
          title={t('procurement.activeHint')}
        >
          <span className="proc-chip-dot" />
          {t('procurement.filterActive')}
        </button>

        <select value={fType} onChange={e => setFType(e.target.value)}>
          <option value="">{t('common.all')}</option>
          <option value="warehouse">{t('procurement.warehouseReq')}</option>
          <option value="purchase">{t('procurement.purchaseOrder')}</option>
        </select>

        <select value={fStatus} onChange={e => setFStatus(e.target.value)}>
          <option value="">{t('myRequests.allStatuses')}</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>

        <select value={fDept} onChange={e => setFDept(e.target.value)}>
          <option value="">{t('procurement.filterAllDepts')}</option>
          {departments.map(d => (
            <option key={d.id} value={d.name}>
              {lang === 'en' ? t('departments.' + d.name, d.name) : d.name}
            </option>
          ))}
        </select>

        <select value={fPeriod} onChange={e => setFPeriod(e.target.value)}>
          <option value="">{t('procurement.filterAllTime')}</option>
          <option value="7d">{t('procurement.period7d')}</option>
          <option value="30d">{t('procurement.period30d')}</option>
          <option value="this_month">{t('procurement.periodThisMonth')}</option>
          <option value="last_month">{t('procurement.periodLastMonth')}</option>
          <option value="90d">{t('procurement.period90d')}</option>
          <option value="this_year">{t('procurement.periodThisYear')}</option>
        </select>

        <span className="proc-count">{list.length} {t('procurement.resultSuffix')}</span>

        {(fType || fStatus || fDept || fPeriod || fActive) && (
          <button
            type="button"
            className="proc-clear"
            onClick={() => { setFType(''); setFStatus(''); setFDept(''); setFPeriod(''); setFActive(false); }}
          >
            <Icon name="close" size={13} /> {t('procurement.clearFilters')}
          </button>
        )}
      </div>

      {loading ? <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>{t('common.loading')}</div> : (
        <div className="proc-list">
          {list.length === 0 && <div style={{ textAlign:'center', padding:48, color:'#9ca3af' }}>{t('common.noData')}</div>}
          {list.map(pr => {
            const st = STATUS_MAP[pr.status] || STATUS_MAP.pending_head;
            return (
              <div key={pr.id} className="proc-card" onClick={() => openDetail(pr)}>
                <div className="proc-card-top">
                  <span className="proc-num">{pr.request_number}</span>
                  <span className={'proc-type ' + pr.type}>{TYPE_MAP[pr.type]?.short || pr.type}</span>
                  <span className="proc-status" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                  {pr.status !== 'completed' && pr.status !== 'rejected' && (
                    <DeliveryTimer startedAt={pr.process_started_at} deliveredAt={pr.status === "completed" ? pr.updated_at : pr.delivered_at} neededBy={pr.needed_by} dayLabel={t("procurement.daySuffix")} notStartedLabel={t("procurement.timerNotStarted")} />
                  )}
                  {pr.rating && <span className="star-row">{[1,2,3,4,5].map(n => <Icon key={n} name="star" size={13} filled={n <= pr.rating} style={{color: n <= pr.rating ? '#f59e0b' : '#d1d5db'}} />)}</span>}
                </div>
                <div className="proc-card-dept">{pr.subject || pr.department} — {pr.item_count || 0} məhsul</div>
                <div className="proc-card-meta">
                  <span>{pr.requester_name}</span>
                  <span>{pr.department}</span>
                  <span>{fmtShort(pr.created_at)}</span>
                  {pr.needed_by && <span style={{color:'#d97706',fontWeight:600}}>Lazımdır: {fmtShort(pr.needed_by)}</span>}
                  {pr.delivered_at && <span>Təhvil: {fmtShort(pr.delivered_at)}</span>}
                </div>
                {/* Mini tracking bar */}
                <div className="mini-track">
                  {[
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4v16h16v-7"/><path d="M18 2l4 4-10 10H8v-4L18 2z"/></svg>,
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="7" r="4"/><path d="M5.5 21v-2a6.5 6.5 0 0113 0v2"/></svg>,
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M3 7v14M21 7v14M6 11h4m4 0h4M6 15h4m4 0h4M6 3h12a1 1 0 011 1v3H5V4a1 1 0 011-1z"/></svg>,
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.2-8.6"/><path d="M21 3v6h-6"/></svg>,
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>,
                ].map((icon, i) => {
                    const statusOrder = ['pending_head','pending_finance','pending_director','approved','in_process','delivered','completed'];
                    const currentStep = statusOrder.indexOf(pr.status);
                    const isDone = i <= currentStep + 1;
                    const isRejected = pr.status === 'rejected';
                    /* Sorğunun hazırda gözlədiyi mərhələ yanıb-sönür —
                       hansı addımda dayandığı bir baxışda görünsün */
                    const isActive = !isRejected && pr.status !== 'completed'
                                     && i === currentStep + 1;
                    return (
                      <React.Fragment key={i}>
                        <div className={'mini-dot'
                            + (isRejected && i > 0 ? ' rej' : isDone ? ' done' : '')
                            + (isActive ? ' now' : '')}
                          title={['Yaradıldı','Rəhbər','Maliyyə','Direktor','Proses','Çatdırılma','Tamamlandı'][i]}>
                          {icon}
                        </div>
                        {i < 6 && <div className={'mini-line' + (isDone && i < currentStep + 1 ? ' done' : '')} />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>

      {/* New Request Modal */}
      {showNew && (
        <div className="modal-overlay" onClick={() => setShowNew(false)}>
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h3>{t('procurement.modalNewTitle')}</h3>
              <button onClick={() => setShowNew(false)}><Icon name="close" size={16} /></button>
            </div>
            <form onSubmit={submitNew} className="modal-body">
              <div style={{ display:'flex', gap:8 }}>
                {[
                  { v:'warehouse', l:t('procurement.warehouseReq'),  d:t('procurement.warehouseReqDesc'),  icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 21V8l9-5 9 5v13"/><path d="M9 21V12h6v9"/></svg> },
                  { v:'purchase',  l:t('procurement.purchaseOrder'), d:t('procurement.purchaseOrderDesc'), icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg> },
                ].map(it => (
                  <label key={it.v} style={{
                    flex:1, border:'1.5px solid '+(newType===it.v?'#007c82':'#e5e7eb'),
                    background:newType===it.v?'#e0f2f4':'#fff',
                    borderRadius:8, padding:'10px 14px', cursor:'pointer',
                    display:'flex', alignItems:'center', gap:10
                  }}>
                    <input type="radio" style={{display:'none'}} checked={newType===it.v} onChange={() => setNewType(it.v)} />
                    <span style={{color:newType===it.v?'#007c82':'#6b7280'}}>{it.icon}</span>
                    <div>
                      <div style={{fontWeight:700,fontSize:'0.88rem',color:newType===it.v?'#007c82':'#374151'}}>{it.l}</div>
                      <div style={{fontSize:'0.72rem',color:'#9ca3af'}}>{it.d}</div>
                    </div>
                  </label>
                ))}
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label>{t('procurement.fieldDept')}</label>
                  <select value={newDept} onChange={e => setNewDept(e.target.value)} required
                    style={{width:'100%',padding:'8px 11px',border:'1px solid #d1d5db',borderRadius:8,fontSize:'0.88rem',boxSizing:'border-box'}}>
                    <option value="">{t('procurement.selectDept')}</option>
                    {departments.map(d => <option key={d.id} value={d.name}>{lang === 'en' ? t('departments.' + d.name, d.name) : d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label>{t('procurement.fieldNeededDate')}</label>
                  <input type="date" value={newNeededBy} onChange={e => setNewNeededBy(e.target.value)} required
                    style={{width:'100%',padding:'8px 11px',border:'1px solid #d1d5db',borderRadius:8,fontSize:'0.88rem',boxSizing:'border-box'}} />
                </div>
              </div>

              <label>{t('procurement.fieldOrderSubject')}</label>
              <input value={newSubject} onChange={e => setNewSubject(e.target.value)} required placeholder={t('procurement.orderSubjectPlaceholder')} />

              <label>{t('procurement.fieldProducts')}</label>
              <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:10, padding:12, overflowX:'auto' }}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.82rem'}}>
                  <thead>
                    <tr style={{borderBottom:'2px solid #e5e7eb'}}>
                      <th style={{padding:'6px 4px',textAlign:'left',fontWeight:700,color:'#6b7280',fontSize:'0.75rem'}}>{t('procurement.colNum')}</th>
                      <th style={{padding:'6px 4px',textAlign:'left',fontWeight:700,color:'#6b7280',fontSize:'0.75rem',minWidth:160}}>{t('procurement.colProductName')}</th>
                      <th style={{padding:'6px 4px',textAlign:'left',fontWeight:700,color:'#6b7280',fontSize:'0.75rem',minWidth:70}}>{t('procurement.colProductCode')}</th>
                      <th style={{padding:'6px 4px',textAlign:'left',fontWeight:700,color:'#6b7280',fontSize:'0.75rem',width:90}}>{t('procurement.colUnit')}</th>
                      <th style={{padding:'6px 4px',textAlign:'left',fontWeight:700,color:'#6b7280',fontSize:'0.75rem',width:65}}>{t('procurement.colQty')}</th>
                      {newType === 'purchase' && <th style={{padding:'6px 4px',textAlign:'left',fontWeight:700,color:'#6b7280',fontSize:'0.75rem',width:85}}>{t('procurement.colPrice')}</th>}
                      <th style={{padding:'6px 4px',textAlign:'left',fontWeight:700,color:'#6b7280',fontSize:'0.75rem',minWidth:110}}>{t('procurement.colUserUsing')}</th>
                      <th style={{padding:'6px 4px',textAlign:'left',fontWeight:700,color:'#6b7280',fontSize:'0.75rem',minWidth:100}}>{t('procurement.colNote')}</th>
                      <th style={{width:30}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newItems.map((it, i) => (
                      <tr key={i} style={{borderBottom:'1px solid #f3f4f6'}}>
                        <td style={{padding:'4px',color:'#9ca3af',fontWeight:700}}>{i+1}</td>
                        <td style={{padding:'4px'}}>
                          <WarehousePicker
                            value={it.item_description}
                            items={whItems}
                            onChange={v => updateItem(i,'item_description',v)}
                            onPick={item => pickWarehouseItem(i, item)}
                            placeholder={t('procurement.productNamePlaceholder')}
                          />
                          {it.wh_stock != null && Number(it.quantity_requested) > Number(it.wh_stock) && (
                            <span className="wh-over">
                              <Icon name="warning" size={11} />
                              {t('procurement.overStock')} ({it.wh_stock})
                            </span>
                          )}
                        </td>
                        <td style={{padding:'4px'}}><input value={it.item_code} onChange={e => updateItem(i,'item_code',e.target.value)} placeholder={t('procurement.productCodePlaceholder')} style={{width:'100%',padding:'5px 7px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem',boxSizing:'border-box'}} /></td>
                        <td style={{padding:'4px'}}>
                          <select value={it.uom} onChange={e => updateItem(i,'uom',e.target.value)} style={{width:'100%',padding:'5px 4px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem'}}>
                            {UOM_VALUES.map(u => <option key={u} value={u}>{UOM_LABELS[u]}</option>)}
                          </select>
                        </td>
                        <td style={{padding:'4px'}}><input type="number" min="1" value={it.quantity_requested} onChange={e => updateItem(i,'quantity_requested',e.target.value)} style={{width:'100%',padding:'5px 7px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem',boxSizing:'border-box'}} /></td>
                        {newType === 'purchase' && <td style={{padding:'4px'}}><input type="number" min="0" step="0.01" value={it.unit_price} onChange={e => updateItem(i,'unit_price',e.target.value)} placeholder="₼" style={{width:'100%',padding:'5px 7px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem',boxSizing:'border-box'}} /></td>}
                        <td style={{padding:'4px'}}><input value={it.used_by} onChange={e => updateItem(i,'used_by',e.target.value)} placeholder={t('procurement.userPlaceholder')} style={{width:'100%',padding:'5px 7px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem',boxSizing:'border-box'}} /></td>
                        <td style={{padding:'4px'}}><input value={it.note || ''} onChange={e => updateItem(i,'note',e.target.value)} placeholder={t('procurement.notePlaceholder')} style={{width:'100%',padding:'5px 7px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.82rem',boxSizing:'border-box'}} /></td>
                        <td style={{padding:'4px'}}><button type="button" className="item-remove" onClick={() => removeItemRow(i)}><Icon name="close" size={16} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
                  <button type="button" className="item-add" onClick={addItemRow}>{t('procurement.addRow')}</button>
                  {newType === 'purchase' && (
                    <span style={{fontSize:'0.82rem',fontWeight:700,color:'#007c82'}}>
                      {t('procurement.colTotal')}: {newItems.reduce((sum, it) => sum + (parseFloat(it.unit_price || 0) * parseInt(it.quantity_requested || 0)), 0).toFixed(2)} ₼
                    </span>
                  )}
                </div>
              </div>

              <label>{t('procurement.additionalNote')}</label>
              <textarea value={newNotes} onChange={e => setNewNotes(e.target.value)} rows={2} placeholder={t('procurement.additionalNotePlaceholder')} />

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowNew(false)}>{t('procurement.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? t('common.saving') : t('procurement.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {/* Vəziyyəti e-poçtla paylaşmaq */}
      {shareOpen && detail && (
        <div className="modal-overlay" style={{zIndex:1100}} onClick={() => setShareOpen(false)}>
          <div className="modal" style={{maxWidth:460}} onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h3>{t('procurement.shareTitle')}</h3>
              <button onClick={() => setShareOpen(false)}><Icon name="close" size={16} /></button>
            </div>
            <form onSubmit={doShare} className="modal-body">
              <p style={{fontSize:'0.83rem',color:'#64748b',margin:'0 0 14px',lineHeight:1.55}}>
                {t('procurement.shareDesc')}
                <b style={{color:'#0f172a'}}> {detail.request_number}</b>
              </p>

              <label>{t('procurement.shareTo')}</label>
              <input
                type="text" value={shareTo} onChange={e => setShareTo(e.target.value)}
                placeholder="ad@mtk.edu.az" required autoFocus
              />
              <small style={{display:'block',color:'#94a3b8',fontSize:'0.72rem',marginTop:4}}>
                {t('procurement.shareToHint')}
              </small>

              <label style={{marginTop:14}}>{t('procurement.shareNote')}</label>
              <textarea
                rows={3} value={shareNote} onChange={e => setShareNote(e.target.value)}
                placeholder={t('procurement.shareNotePlaceholder')}
              />

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShareOpen(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary" disabled={shareBusy}>
                  {shareBusy ? t('procurement.shareSending') : t('procurement.shareSend')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal modal-xl" onClick={e => e.stopPropagation()} style={{maxHeight:'94vh',overflowY:'auto'}}>
            <div className="modal-hdr">
              <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                <span className="proc-num" style={{fontSize:'0.85rem'}}>{detail.request_number}</span>
                <span className={'proc-type ' + detail.type}>{TYPE_MAP[detail.type]?.label}</span>
                <span className="proc-status" style={{ background: (STATUS_MAP[detail.status]||{}).bg, color: (STATUS_MAP[detail.status]||{}).color }}>
                  {(STATUS_MAP[detail.status]||{}).label}
                </span>
                {/* Paylaş nişanların yanındadır — sağ küncdə yalnız bağla qalır */}
                <button type="button" className="proc-share-btn" onClick={() => { setShareTo(''); setShareNote(''); setShareOpen(true); }}>
                  <Icon name="message" size={14} /> {t('procurement.shareBtn')}
                </button>
              </div>
              <button onClick={() => setDetail(null)}><Icon name="close" size={16} /></button>
            </div>

            <div style={{padding:'0 22px'}}>
              <ApprovalSteps req={detail} t={t} lang={lang} />
            </div>

            {/* Timer */}
            {detail.process_started_at && (
              <div style={{padding:'0 22px 10px',display:'flex',alignItems:'center',gap:10}}>
                <span style={{fontSize:'0.82rem',fontWeight:600,color:'#374151'}}>
                  {t('procurement.waitTime')}
                </span>
                <DeliveryTimer startedAt={detail.process_started_at} deliveredAt={detail.status==="completed"?detail.updated_at:detail.delivered_at} neededBy={detail.needed_by} dayLabel={t("procurement.daySuffix")} notStartedLabel={t("procurement.timerNotStarted")} />
              </div>
            )}

            {/* Info */}
            <div style={{padding:'0 22px 14px'}}>
              <div className="proc-detail-grid">
                <div><label>{t('procurement.labelOrderer')}</label><br/><span>{detail.requester_name}</span></div>
                <div><label>{t('procurement.labelDept')}</label><br/><span>{lang === 'en' ? t('departments.' + detail.department, detail.department) : detail.department}</span></div>
                <div><label>{t('procurement.labelCreated')}</label><br/><span>{fmt(detail.created_at)}</span></div>
                <div><label>{t('procurement.labelType')}</label><br/><span>{TYPE_MAP[detail.type]?.label}</span></div>
                {detail.subject && <div style={{gridColumn:'1/-1'}}><label>{t('procurement.fieldOrderSubject').replace(' *','')}</label><br/><span style={{fontWeight:600}}>{detail.subject}</span></div>}
                {detail.needed_by && <div><label>{t('procurement.fieldNeededDate').replace(' *','')}</label><br/><span style={{fontWeight:600,color:'#d97706'}}>{new Date(detail.needed_by).toLocaleDateString(lang === 'en' ? 'en-US' : 'az-AZ')}</span></div>}
              </div>
              {detail.notes && <div style={{marginTop:10,padding:'10px 12px',background:'#f9fafb',borderRadius:8,fontSize:'0.85rem',color:'#374151'}}><b>{t('procurement.notePrefix')}</b> {detail.notes}</div>}
            </div>

            {/* Items */}
            <div style={{padding:'0 22px 14px'}}>
              <table className="items-table">
                <thead>
                  <tr>
                    <th>{t('procurement.colNum')}</th>
                    <th>{t('common.name')}</th>
                    <th>{t('procurement.colProductCode')}</th>
                    <th>{t('procurement.colUnit')}</th>
                    <th>{t('procurement.colQty')}</th>
                    {detail.type === 'purchase' && <th>{t('procurement.colPrice')}</th>}
                    {detail.type === 'purchase' && <th>{t('procurement.colTotal')}</th>}
                    {detail.status === 'delivered' && detail.type === 'warehouse' ? <th>{t('procurement.colQty')}</th> : null}
                    <th>{t('procurement.colUserUsing')}</th>
                    <th>{t('procurement.colNote')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it.id}>
                      <td>{i + 1}</td>
                      <td style={{fontWeight:600}}>{it.item_description}</td>
                      <td style={{fontFamily:'monospace',color:'#6b7280'}}>{it.item_code || '—'}</td>
                      <td>{it.uom}</td>
                      <td style={{fontWeight:700}}>{it.quantity_requested}</td>
                      {detail.type === 'purchase' && <td>{it.unit_price ? Number(it.unit_price).toFixed(2) + ' ₼' : '—'}</td>}
                      {detail.type === 'purchase' && <td style={{fontWeight:700,color:'#007c82'}}>{it.unit_price ? (Number(it.unit_price) * it.quantity_requested).toFixed(2) + ' ₼' : '—'}</td>}
                      {detail.status === 'delivered' && detail.type === 'warehouse' ? (
                        <td>
                          <input type="number" min="0" max={it.quantity_requested}
                            value={receiveQtys[it.id] || ''}
                            onChange={e => setReceiveQtys(p => ({...p, [it.id]: e.target.value}))}
                            style={{width:60,padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:6,fontSize:'0.85rem'}} />
                        </td>
                      ) : null}
                      <td>{it.used_by || '—'}</td>
                      <td style={{fontSize:'0.8rem',color:'#6b7280'}}>{it.note || '—'}</td>
                    </tr>
                  ))}
                  {detail.type === 'purchase' && (
                    <tr style={{borderTop:'2px solid #007c82'}}>
                      <td colSpan={6} style={{textAlign:'right',fontWeight:700,color:'#374151',fontSize:'0.88rem'}}>{t('procurement.grandTotal')}</td>
                      <td style={{fontWeight:800,color:'#007c82',fontSize:'0.95rem'}}>{items.reduce((s, it) => s + (Number(it.unit_price||0) * it.quantity_requested), 0).toFixed(2)} ₼</td>
                      <td colSpan={2}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div style={{padding:'0 22px 18px',display:'flex',flexDirection:'column',gap:10}}>
              {/* Approval actions */}
              {(canApprove('head') || canApprove('finance') || canApprove('director')) && (
                <div style={{background:'#f9fafb',border:'1px solid #e5e7eb',borderRadius:10,padding:14,display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{fontWeight:700,fontSize:'0.88rem',color:'#111827'}}>
                    {canApprove('head') ? 'Rəhbər Heyəti Təsdiqi' : canApprove('finance') ? 'Maliyyə Təsdiqi' : 'Direktor Təsdiqi'}
                  </div>
                  <input value={approveNote} onChange={e => setApproveNote(e.target.value)} placeholder="Qeyd (istəyə bağlı)..."
                    style={{padding:'7px 10px',border:'1px solid #d1d5db',borderRadius:7,fontSize:'0.85rem'}} />
                  <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                    <button className="btn-cancel" onClick={() => {
                      const stage = canApprove('head') ? 'head' : canApprove('finance') ? 'finance' : 'director';
                      doReject(stage);
                    }}>Rədd et</button>
                    <button className="btn-primary" onClick={() => {
                      const stage = canApprove('head') ? 'head' : canApprove('finance') ? 'finance' : 'director';
                      doApprove(stage);
                    }}>Təsdiqlə</button>
                  </div>
                </div>
              )}

              {/* İcraya başla — taymer buradan işə düşür.
                  Çatdırılma düyməsi yalnız bundan sonra görünür ki,
                  sayğac işin həqiqi müddətini göstərsin. */}
              {(isAdmin || isDeptHead) && detail.status === 'approved' && (
                <button className="btn-primary" onClick={doStart} style={{background:'#0369a1'}}>
                  {t('procurement.btnStartAction')}
                </button>
              )}

              {/* Deliver */}
              {(isAdmin || isDeptHead) && detail.status === 'in_process' && (
                <button className="btn-primary" onClick={doDeliver} style={{background:'#059669'}}>
                  {t('procurement.btnDeliverAction')}
                </button>
              )}

              {/* Receive */}
              {detail.status === 'delivered' && (detail.requester_id === user.id || isAdmin) && (
                <div style={{background:'#ecfdf5',border:'1px solid #6ee7b7',borderRadius:10,padding:14,display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{fontWeight:700,fontSize:'0.88rem',color:'#059669'}}>Təhvil-Təslim</div>
                  <p style={{fontSize:'0.85rem',color:'#374151',margin:0}}>
                    {detail.type === 'warehouse' ? 'Faktiki verilən miqdarı daxil edib təsdiqləyin.' : 'Malları qəbul etdiyinizi təsdiqləyin.'}
                  </p>
                  <button className="btn-primary" onClick={doReceive} style={{alignSelf:'flex-end'}}>Qəbul etdim</button>
                </div>
              )}

              {/* Rate */}
              {detail.status === 'completed' && !detail.rating && (detail.requester_id === user.id || isAdmin) && (
                <div style={{background:'#fffbeb',border:'1px solid #fcd34d',borderRadius:10,padding:14,display:'flex',flexDirection:'column',gap:8,alignItems:'center'}}>
                  <div style={{fontWeight:700,color:'#92400e'}}>Xidməti Qiymətləndirin</div>
                  <div style={{display:'flex',gap:4}}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} type="button" onClick={() => setRateVal(n)}
                        style={{background:'none',border:'none',fontSize:'1.8rem',cursor:'pointer',color:n<=rateVal?'#f59e0b':'#d1d5db',lineHeight:0}}><Icon name="star" size={26} filled /></button>
                    ))}
                  </div>
                  <input value={rateComment} onChange={e => setRateComment(e.target.value)} placeholder="Şərh (istəyə bağlı)..."
                    style={{width:'100%',padding:'7px 10px',border:'1px solid #d1d5db',borderRadius:7,fontSize:'0.85rem',boxSizing:'border-box'}} />
                  <button className="btn-primary" onClick={doRate}>Qiymətləndir</button>
                </div>
              )}

              {detail.rating && (
                <div style={{textAlign:'center',padding:8}}>
                  <span className="star-row">{[1,2,3,4,5].map(n => <Icon key={n} name="star" size={18} filled={n <= detail.rating} style={{color: n <= detail.rating ? '#f59e0b' : '#d1d5db'}} />)}</span>
                  {detail.rating_comment && <div style={{fontSize:'0.82rem',color:'#6b7280',fontStyle:'italic',marginTop:4}}>"{detail.rating_comment}"</div>}
                </div>
              )}

              {/* PDF Download */}
              {['completed','delivered','approved'].includes(detail.status) && (
                <div style={{textAlign:'center',paddingTop:8}}>
                  <button onClick={async () => {
                    try {
                      const res = await API.get('/procurement/' + detail.id + '/pdf', { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement('a');
                      a.href = url; a.download = detail.request_number + '.pdf'; a.click();
                      window.URL.revokeObjectURL(url);
                    } catch(e) { alert(t('procurement.pdfFailed')); }
                  }}
                    style={{
                      display:'inline-flex',alignItems:'center',gap:8,
                      padding:'10px 24px',borderRadius:8,border:'none',cursor:'pointer',
                      background:'#007c82',color:'#fff',
                      fontSize:'0.88rem',fontWeight:600,
                    }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    {t('procurement.btnDownloadPdf')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
